import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  FileCheck,
  Users,
  AlertTriangle,
  CheckCircle2,
  Clock,
  X,
  Search,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  BarChart3,
  User,
  FileText,
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type AttestationType = 'acknowledged' | 'understood' | 'accepted';
type AttestationStatus = 'attested' | 'pending' | 'overdue';

interface PolicyAttestation {
  id: number;
  policy_name: string;
  policy_version: string;
  total_users: number;
  attested_count: number;
  pending_count: number;
  overdue_count: number;
  percent_attested: number;
  last_triggered: string | null;
  users?: UserAttestation[];
}

interface UserAttestation {
  user_id: number;
  user_name: string;
  email: string;
  status: AttestationStatus;
  attestation_type: AttestationType | null;
  attested_at: string | null;
}

interface UserSummary {
  user_id: number;
  user_name: string;
  email: string;
  total_policies: number;
  attested: number;
  pending: number;
  overdue: number;
}

interface DashboardStats {
  attestation_rate: number;
  overdue_re_attestations: number;
  policies_needing_re_attestation: number;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STATUS_BADGE: Record<AttestationStatus, string> = {
  attested: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  overdue: 'bg-red-100 text-red-800',
};

const TYPE_BADGE: Record<AttestationType, string> = {
  acknowledged: 'bg-blue-50 text-blue-700',
  understood: 'bg-indigo-50 text-indigo-700',
  accepted: 'bg-teal-50 text-teal-700',
};

const fmtLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '--';

/* ------------------------------------------------------------------ */
/*  API helpers                                                        */
/* ------------------------------------------------------------------ */

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const API = '/api/compliance/policy-attestations';

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(url, { headers: authHeaders(), ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const PolicyAttestations: React.FC = () => {
  const [view, setView] = useState<'policy' | 'user'>('policy');
  const [policies, setPolicies] = useState<PolicyAttestation[]>([]);
  const [userSummaries, setUserSummaries] = useState<UserSummary[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Expanded policy row
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<UserAttestation[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Re-attestation
  const [triggeringId, setTriggeringId] = useState<number | null>(null);

  /* ---------- Load ---------- */

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      const qs = params.toString() ? `?${params}` : '';

      const [listRes, statsRes] = await Promise.all([
        apiFetch<any>(`${API}${qs}`),
        apiFetch<DashboardStats>(`${API}/dashboard`),
      ]);

      const data = listRes.policies ?? listRes.data ?? listRes;
      if (Array.isArray(data)) {
        setPolicies(data);
      }

      // User summaries
      const users = listRes.users ?? listRes.user_summaries ?? [];
      setUserSummaries(Array.isArray(users) ? users : []);

      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => { load(); }, [load]);

  /* ---------- Expand policy ---------- */

  const toggleExpand = async (policy: PolicyAttestation) => {
    if (expandedId === policy.id) {
      setExpandedId(null);
      setExpandedUsers([]);
      return;
    }
    setExpandedId(policy.id);
    setExpandedUsers([]);
    setDetailLoading(true);
    try {
      const res = await apiFetch<any>(`${API}/${policy.id}`);
      setExpandedUsers(res.users ?? res.attestations ?? res.data ?? []);
    } catch {
      // silent
    } finally {
      setDetailLoading(false);
    }
  };

  /* ---------- Trigger re-attestation ---------- */

  const handleTriggerReAttestation = async (policyId: number) => {
    setTriggeringId(policyId);
    try {
      await apiFetch(`${API}/${policyId}/re-attest`, { method: 'POST' });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setTriggeringId(null);
    }
  };

  /* ---------- Helpers ---------- */

  const getProgressColor = (pct: number) => {
    if (pct >= 90) return 'bg-green-500';
    if (pct >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  /* ---------- Render ---------- */

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-xl">
            <FileCheck size={24} className="text-teal-600" />
          </div>
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Policy Attestations</h1><PageInfoButton /></div>
            <p className="text-sm text-slate-500">Track policy acknowledgement and attestation compliance</p>
          </div>
        </div>
        <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 size={16} className="text-teal-600" />
              <p className="text-sm text-slate-500">Attestation Rate</p>
            </div>
            <div className="flex items-end gap-3">
              <p className="text-3xl font-bold text-slate-900">{stats.attestation_rate}%</p>
              <div className="flex-1 mb-2">
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${getProgressColor(stats.attestation_rate)}`} style={{ width: `${stats.attestation_rate}%` }} />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-red-50 border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-red-600" />
              <p className="text-sm text-slate-500">Overdue Re-attestations</p>
            </div>
            <p className="text-3xl font-bold text-red-700">{stats.overdue_re_attestations}</p>
          </div>
          <div className="bg-amber-50 border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <RotateCcw size={16} className="text-amber-600" />
              <p className="text-sm text-slate-500">Policies Needing Re-attestation</p>
            </div>
            <p className="text-3xl font-bold text-amber-700">{stats.policies_needing_re_attestation}</p>
          </div>
        </div>
      )}

      {/* View toggle + search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {(['policy', 'user'] as const).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                view === v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {v === 'policy' ? (
                <span className="flex items-center gap-1.5"><FileText size={14} /> By Policy</span>
              ) : (
                <span className="flex items-center gap-1.5"><Users size={14} /> By User</span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder={view === 'policy' ? 'Search policies...' : 'Search users...'}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm flex-1 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
          />
        </div>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
        </div>
      ) : view === 'policy' ? (
        /* By Policy View */
        policies.length === 0 ? (
          <div className="py-16 bg-white border border-slate-200 rounded-xl">
            <FileCheck size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg font-medium">No policy attestation data</p>
            <p className="text-slate-400 text-sm mt-1">Attestation data will appear once policies require acknowledgement.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {policies.map(policy => {
              const isExpanded = expandedId === policy.id;
              return (
                <div key={policy.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow transition-shadow">
                  <div className="flex items-center gap-4 px-5 py-4 cursor-pointer" onClick={() => toggleExpand(policy)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{policy.policy_name}</span>
                        <span className="text-xs text-slate-400">v{policy.policy_version}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
                        <span>{policy.total_users} users</span>
                        <span className="text-green-600">{policy.attested_count} attested</span>
                        <span className="text-amber-600">{policy.pending_count} pending</span>
                        {policy.overdue_count > 0 && <span className="text-red-600">{policy.overdue_count} overdue</span>}
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="w-32 hidden sm:block">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${getProgressColor(policy.percent_attested)}`} style={{ width: `${policy.percent_attested}%` }} />
                        </div>
                        <span className="text-xs font-medium text-slate-600 w-10 text-right">{policy.percent_attested}%</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <button
                      onClick={e => { e.stopPropagation(); handleTriggerReAttestation(policy.id); }}
                      disabled={triggeringId === policy.id}
                      className="hidden md:flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition disabled:opacity-50"
                      title="Trigger re-attestation for all users"
                    >
                      <RotateCcw size={12} /> {triggeringId === policy.id ? 'Triggering...' : 'Re-attest'}
                    </button>

                    <button className="text-slate-400 hover:text-slate-600 flex-shrink-0">
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>

                  {/* Expanded user list */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50 px-5 py-4">
                      {detailLoading ? (
                        <div className="flex justify-center py-6">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600" />
                        </div>
                      ) : expandedUsers.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">No attestation records found.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-slate-500 uppercase tracking-wide">
                                <th className="text-left pb-2 pr-4">User</th>
                                <th className="text-left pb-2 pr-4">Status</th>
                                <th className="text-left pb-2 pr-4">Type</th>
                                <th className="text-left pb-2">Attested At</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {expandedUsers.map(u => (
                                <tr key={u.user_id} className="text-slate-700">
                                  <td className="py-2.5 pr-4 text-left">
                                    <div className="flex items-center gap-2">
                                      <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                                        <User size={14} className="text-slate-500" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-slate-900 text-sm">{u.user_name}</p>
                                        <p className="text-xs text-slate-400">{u.email}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-2.5 pr-4 text-left">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[u.status]}`}>
                                      {u.status === 'attested' && <CheckCircle2 size={11} className="mr-1" />}
                                      {u.status === 'overdue' && <AlertTriangle size={11} className="mr-1" />}
                                      {u.status === 'pending' && <Clock size={11} className="mr-1" />}
                                      {fmtLabel(u.status)}
                                    </span>
                                  </td>
                                  <td className="py-2.5 pr-4 text-left">
                                    {u.attestation_type ? (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_BADGE[u.attestation_type]}`}>
                                        {fmtLabel(u.attestation_type)}
                                      </span>
                                    ) : (
                                      <span className="text-slate-400 text-xs">--</span>
                                    )}
                                  </td>
                                  <td className="py-2.5 text-left text-xs text-slate-500">{fmtDate(u.attested_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* By User View */
        userSummaries.length === 0 ? (
          <div className="py-16 bg-white border border-slate-200 rounded-xl">
            <Users size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg font-medium">No user attestation data</p>
            <p className="text-slate-400 text-sm mt-1">User attestation summaries will appear here.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['User', 'Total Policies', 'Attested', 'Pending', 'Overdue', 'Completion'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {userSummaries.map(u => {
                    const pct = u.total_policies > 0 ? Math.round((u.attested / u.total_policies) * 100) : 0;
                    return (
                      <tr key={u.user_id} className="hover:bg-slate-50 transition">
                        <td className="px-4 py-3 text-left">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                              <User size={14} className="text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{u.user_name}</p>
                              <p className="text-xs text-slate-400">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-left text-slate-700">{u.total_policies}</td>
                        <td className="px-4 py-3 text-left">
                          <span className="inline-flex items-center gap-1 text-green-700">
                            <CheckCircle2 size={14} /> {u.attested}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-left">
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <Clock size={14} /> {u.pending}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-left">
                          {u.overdue > 0 ? (
                            <span className="inline-flex items-center gap-1 text-red-700 font-medium">
                              <AlertTriangle size={14} /> {u.overdue}
                            </span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-left">
                          <div className="flex items-center gap-2 w-28">
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${getProgressColor(pct)}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs font-medium text-slate-600 w-8 text-right">{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default PolicyAttestations;
